import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import {
  ConsentManagerDialog,
  ConsentManagerProvider,
  CookieBanner,
} from '@c15t/react';

import { useAuth } from '@/hooks/useAuth';
import { recordConsentChange } from '@/services/CookieConsentService';
import { gtmService } from '@/services/GTMService';
import { postHogTrackingService } from '@/services/PostHogTrackingService';

export const CookieManagerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user, getToken } = useAuth();

  const hasInitializedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  const handleConsentSet = useCallback(
    ({ preferences }: { preferences: Record<string, boolean> }) => {
      postHogTrackingService.updateConsent(preferences);
      gtmService.updateConsent(preferences);

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        return;
      }

      const userId = userIdRef.current;

      recordConsentChange(userId, preferences, userId ? getToken : undefined);
    },
    [getToken]
  );

  return (
    <ConsentManagerProvider
      options={{
        mode: 'offline',
        consentCategories: ['necessary', 'measurement', 'marketing'],
        callbacks: {
          onConsentSet: handleConsentSet,
        },
      }}
    >
      <CookieBanner />
      <ConsentManagerDialog theme={{ 'dialog.footer': 'hidden invisible' }} />
      {children}
    </ConsentManagerProvider>
  );
};
